/**
 * BarChart - Bar chart wrapper using react-chartjs-2 with EduQ brand colors
 * 
 * Features:
 * - EduQ brand color scheme
 * - Responsive design
 * - Horizontal/vertical orientation
 * - Customizable options
 * - Loading state
 * - Empty state
 */

import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// EduQ brand colors
const BRAND_COLORS = {
  blue: '#0066FF',
  cyan: '#00D9B5',
  blueHover: '#0052CC',
  cyanHover: '#00B89A',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6',
  secondary: '#6B7280'
};

const DEFAULT_COLORS = [
  BRAND_COLORS.blue,
  BRAND_COLORS.cyan,
  BRAND_COLORS.success,
  BRAND_COLORS.warning,
  BRAND_COLORS.info,
  BRAND_COLORS.error,
  BRAND_COLORS.secondary
];

const BarChart = ({
  data,
  title,
  height = 300,
  loading = false,
  emptyMessage = 'No data available',
  options = {},
  colors = DEFAULT_COLORS,
  horizontal = false,
  stacked = false,
  showLegend = true,
  showGrid = true,
  className = ''
}) => {
  if (loading) {
    return (
      <div className={`admin-chart admin-chart--loading ${className}`} style={{ height }}>
        <div className="admin-chart__loading">
          <div className="admin-spinner"></div>
          <p>Loading chart...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.labels || data.labels.length === 0) {
    return (
      <div className={`admin-chart admin-chart--empty ${className}`} style={{ height }}>
        <div className="admin-chart__empty">
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Apply colors to datasets
  const chartData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || colors[index % colors.length],
      borderColor: dataset.borderColor || colors[index % colors.length],
      borderWidth: dataset.borderWidth || 0,
      borderRadius: dataset.borderRadius || 4,
      hoverBackgroundColor: dataset.hoverBackgroundColor || (
        colors[index % colors.length] === BRAND_COLORS.blue ? BRAND_COLORS.blueHover :
        colors[index % colors.length] === BRAND_COLORS.cyan ? BRAND_COLORS.cyanHover :
        colors[index % colors.length]
      )
    }))
  };

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }
        }
      },
      title: {
        display: !!title,
        text: title,
        font: {
          size: 16,
          weight: '600',
          family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: '600'
        },
        bodyFont: {
          size: 12
        },
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        displayColors: true,
        usePointStyle: true
      }
    },
    scales: {
      x: {
        stacked: stacked,
        grid: {
          display: showGrid && !horizontal,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        stacked: stacked,
        grid: {
          display: showGrid && horizontal,
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          font: {
            size: 11
          }
        },
        beginAtZero: true
      }
    }
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    plugins: {
      ...defaultOptions.plugins,
      ...options.plugins
    },
    scales: {
      ...defaultOptions.scales,
      ...options.scales
    }
  };

  return (
    <div className={`admin-chart ${className}`} style={{ height }}>
      <Bar data={chartData} options={mergedOptions} />
    </div>
  );
};

export default BarChart;
